<template>
    <section class="radio-comp mb-10 animate__animated animate__fadeIn animate__fast theme-purple-background">
        <app-header v-if="!isParcVersion" @stop="stop" />

        <playing-banner v-if="stationDetails"
                        :loading="isLoading"
                        @mute="mute($event)"
                        @pause="pause($event)"
                        @stop="stop"
                        @update-volume="setVolume($event)">
        </playing-banner>

        <visual-search @play="play($event)"></visual-search>

        <favourites v-if="!isParcVersion" @play="play($event)"/>

        <audio ref="audioPlayer" style="display: none" />
    </section>
</template>

<script>
  import {Howl, Howler} from 'howler';
  import { mapState, mapMutations } from 'vuex';
  import AppHeader from './AppHeader.vue';
  import PlayingBanner from './PlayingBanner';
  import Favourites from './Favourites';
  import VisualSearch from '@/components/search/VisualSearch.vue';
  import Hls from 'hls.js';

  export default {
    props: [''],
    components: {
      AppHeader,
      PlayingBanner,
      Favourites,
      VisualSearch,
    },
    data() {
      return {
        hlsInstance: null,
        howlInstance: null,
        audioPlayingListener: null,
        audioWaitingListener: null,
        audioErrorListener: null,
      };
    },
    computed: {
      ...mapState('radio', {
        currentStation: state => state.currentStation,
        stations: state => state.stations,
        isPlaying: state => state.isPlaying,
        isLoading: state => state.isLoading,
      }),
      ...mapState({
        favourites: state => state.favourites.favourites,
        isAndroid: state => state.isAndroid,
      }),
      stationDetails() {
        return this.currentStation.details;
      },
      isMuted() {
        return this.currentStation.muted;
      },
      currentVolume() {
        return this.currentStation.volume;
      },
      isCurrentStreamHls() {
        return this.stationDetails && this.stationDetails.uri && this.stationDetails.uri.endsWith('.m3u8');
      }
    },
    beforeDestroy() {
      this.stop();
      this.removeAudioElementListeners();
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
    },
    mounted() {
      this.$store.commit('favourites/SET_FAVOURITES_FROM_LOCAL');
      this.setRegionByQuery();
      this.setUpRadio();
    },
    methods: {
      ...mapMutations('radio', [
        'SET_CURRENT_STATION_DETAILS',
        'SET_IS_PLAYING',
        'SET_IS_LOADING',
        'SET_CURRENT_STATION_MUTED',
        'SET_CURRENT_STATION_VOLUME',
      ]),

      removeAudioElementListeners() {
        const audioPlayer = this.$refs.audioPlayer;
        if (!audioPlayer) return;

        if (this.audioPlayingListener) {
          audioPlayer.removeEventListener('playing', this.audioPlayingListener);
          this.audioPlayingListener = null;
        }
        if (this.audioWaitingListener) {
          audioPlayer.removeEventListener('waiting', this.audioWaitingListener);
          this.audioWaitingListener = null;
        }
        if (this.audioErrorListener) {
          audioPlayer.removeEventListener('error', this.audioErrorListener);
          this.audioErrorListener = null;
        }
      },
      setupAudioElementListeners() {
        const audioPlayer = this.$refs.audioPlayer;
        if (!audioPlayer) return;

        this.removeAudioElementListeners();

        this.audioPlayingListener = () => {
          this.SET_IS_LOADING(false);
          this.SET_IS_PLAYING(true);
          // Log successful HLS playback start
          if (this.stationDetails) {
            this.sendLog('PlaybackStarted', `Playback started: ${this.stationDetails.name}`, {
              station: this.stationDetails,
              streamType: 'hls',
              source: 'user_action'
            });
          }
        };
        this.audioWaitingListener = () => {
          this.SET_IS_LOADING(true);
        };
        this.audioErrorListener = (e) => {
          console.error('Audio Element Error:', e);
          this.$toasted.error('Audio playback error.');
          this.SET_IS_LOADING(false);
          this.SET_IS_PLAYING(false);
        };

        audioPlayer.addEventListener('playing', this.audioPlayingListener);
        audioPlayer.addEventListener('waiting', this.audioWaitingListener);
        audioPlayer.addEventListener('error', this.audioErrorListener);
      },
      setUpRadio(){
        if (Howler && Howler._howls && Howler._howls.length > 0) {
          Howler.unload();
        }
        if (this.$refs.audioPlayer) {
          this.$refs.audioPlayer.pause();
          this.$refs.audioPlayer.removeAttribute('src');
          try { this.$refs.audioPlayer.load(); } catch(e) { /* ignore */ }
        }
        this.setDefaultRadio();
      },
      setUpHls(station, streamUrl) {
        const audioPlayer = this.$refs.audioPlayer;
        if (!audioPlayer) {
          console.error("Audio player ref not found");
          this.SET_IS_LOADING(false);
          return;
        }

        if (this.hlsInstance) {
          this.hlsInstance.destroy();
          this.hlsInstance = null;
        }
        audioPlayer.removeAttribute('src');
        audioPlayer.load();
        this.setupAudioElementListeners();

        if (Hls.isSupported()) {
          this.hlsInstance = new Hls();
          this.hlsInstance.loadSource(streamUrl);
          this.hlsInstance.attachMedia(audioPlayer);

          this.hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
            audioPlayer.muted = this.isMuted;
            audioPlayer.play().catch(e => {
              console.error("HLS Playback failed:", e);
              this.$toasted.error('Playback start failed. Interaction might be required.');
              this.SET_IS_LOADING(false);
              this.SET_IS_PLAYING(false);
            });
          });

          this.hlsInstance.on(Hls.Events.ERROR, (event, data) => {
            console.error('HLS.js Error:', event, data);
            if (data.fatal) {
              this.$toasted.error('A critical error occurred with this stream.');
              this.stop();
            } else {
              this.$toasted.warning('A minor stream issue occurred.');
            }
            if (data.type !== Hls.ErrorTypes.NETWORK_ERROR || data.details !== Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
              this.SET_IS_LOADING(false); // Use mutation
            }
          });
        } else if (audioPlayer.canPlayType('application/vnd.apple.mpegurl')) {
          audioPlayer.src = streamUrl;
          audioPlayer.muted = this.isMuted;
          audioPlayer.play().catch(e => {
            console.error("Native HLS Playback failed:", e);
            this.$toasted.error('Playback start failed. Interaction might be required.');
            this.SET_IS_LOADING(false);
            this.SET_IS_PLAYING(false);
          });
        } else {
          this.$toasted.error('HLS playback is not supported.');
          console.error('HLS not supported.');
          this.SET_IS_LOADING(false);
          this.SET_IS_PLAYING(false);
        }
      },
      setRegionByQuery() {
        const urlParams = new URLSearchParams(window.location.search);
        const region = urlParams.get('region');
        if (region) {
          this.$store.dispatch('radio/changeRegion', region);
        }
      },
      setDefaultRadio() {
        if (this.stationDetails) {
          return;
        }
        let defaultStation = null;
        if (this.favourites && this.favourites.length > 0) {
          defaultStation = this.favourites[0];
        } else if (this.stations && this.stations.length > 0) {
          defaultStation = this.stations[0];
        }
        if (defaultStation) {
          this.SET_CURRENT_STATION_DETAILS(defaultStation);
        }
      },
      play(station){
        if (!station || !station.uri) {
          console.warn("Play called with invalid station data:", station);
          return;
        }

        // Log station selection
        this.sendLog('StationSelected', `Station selected: ${station.name}`, {
          station: station,
          source: 'user_selection'
        });

        this.stop();

        this.SET_IS_LOADING(true);
        this.SET_IS_PLAYING(false);
        this.SET_CURRENT_STATION_DETAILS(station);

        let uri = station.uri.trim();
        const isHls = /\.m3u8(\?|$)/i.test(uri);

        this.removeAudioElementListeners();

        if (isHls) {
          this.setUpHls(station, uri);
        } else {
          if (this.$refs.audioPlayer) {
            this.$refs.audioPlayer.removeAttribute('src');
            try { this.$refs.audioPlayer.load(); } catch(e) { /* ignore */ }
          }
          if(station.uri.includes("planetradio")){
            const unixTimestamp = Math.floor(Date.now() / 1000);
            uri = uri + `?aw_0_1st.bauer_loggedin=true&aw_0_1st.skey=${unixTimestamp}`;
          }

          const newHowl = new Howl({
            src: [uri],
            html5: true,
            format: ['mp3', 'aac'],
            volume: this.currentVolume,
            mute: this.isMuted
          });

          this.howlInstance = newHowl;

          newHowl.on('play', () => {
            this.SET_IS_LOADING(false);
            this.SET_IS_PLAYING(true);
            // Log successful playback start
            this.sendLog('PlaybackStarted', `Playback started: ${station.name}`, {
              station: station,
              streamType: 'howler',
              source: 'user_action'
            });
          });
          newHowl.on('loaderror', (id, error) => {
            console.error('Howler Load Error:', id, error);
            this.$toasted.error('Problem loading this station.');
            this.SET_IS_LOADING(false);
            this.SET_IS_PLAYING(false);
            this.howlInstance = null;
          });
          newHowl.on('playerror', (id, error) => {
            console.error('Howler Play Error:', id, error);
            this.$toasted.error('Playback failed. Click play again.');
            this.SET_IS_LOADING(false);
            this.SET_IS_PLAYING(false);
            this.howlInstance = null;
          });

          newHowl.play();
        }
      },
      stop() {
        if (this.hlsInstance) {
          const audioPlayer = this.$refs.audioPlayer;
          if (audioPlayer) {
            audioPlayer.pause();
            audioPlayer.removeAttribute('src');
            try { audioPlayer.load(); } catch(e) { /* ignore */ }
          }
          this.hlsInstance.destroy();
          this.hlsInstance = null;
          this.removeAudioElementListeners();
        }

        if (this.howlInstance) {
          this.howlInstance.unload();
          this.howlInstance = null;
        }

        this.SET_IS_LOADING(false);
        this.SET_IS_PLAYING(false);
      },
      pause(value){
        if (value) {
          // Log pause action
          if (this.stationDetails) {
            this.sendLog('PlaybackPaused', `Playback paused: ${this.stationDetails.name}`, {
              station: this.stationDetails,
              action: 'pause',
              source: 'user_action'
            });
          }
          this.stop();
          return;
        }
        if (!this.stationDetails) {
          return;
        }
        // Log resume action
        this.sendLog('PlaybackResumed',`Playback resumed: ${this.stationDetails.name}`, {
          station: this.stationDetails,
          action: 'resume',
          source: 'user_action'
        });
        this.play(this.stationDetails);
      },
      setVolume(number){
        const isHls = this.isCurrentStreamHls;

        if (isHls) {
          if (this.$refs.audioPlayer) {
            this.$refs.audioPlayer.volume = number;
          }
        } else if (this.howlInstance) {
          this.howlInstance.volume(number);
        }
        this.SET_CURRENT_STATION_VOLUME(number);
      },
      mute(value) {
        const isHls = this.isCurrentStreamHls;

        if (isHls) {
          if (this.$refs.audioPlayer) {
            this.$refs.audioPlayer.muted = value;
          }
        } else if (this.howlInstance) {
          this.howlInstance.mute(value);
        }
        this.SET_CURRENT_STATION_MUTED(value);
      },
      sendLog(action, description, meta = null) {
        const logData = {
          action,
          description,
          meta,
        };

        this.$store.dispatch('radio/sendLog', logData);

        if (window.flutter_inappwebview) {
          const handler = window.flutter_inappwebview.callHandler || window.flutter_inappwebview._callHandler;
          if (typeof handler === 'function') {
            handler("logHandler", logData)
          }
        }
      }
    },
  };
</script>